/**
 * Record feature flag values to Meticulous with minimal overhead.
 *
 * - Record each flag at most once per page load (per-load dedupe).
 * - Keep the evaluation hot path fast
 * - Bound memory and payload size with a high but safe cap and truncation.
 * - Don't interrupt user flow on failure
 */

// Keys of flags recorded during this page load. Values are not stored.
const recordedThisPageLoad = new Set<string>();

// Cap umber of flags tracked to bound memory usage on misconfiguration
const MAX_FLAGS_PER_LOAD = 2000;
// Truncate JSON payloads to keep recording lightweight and predictable.
const MAX_STRING_LENGTH = 1024;

// Normalize to the string|boolean types accepted by Meticulous. Objects/arrays are JSON-stringified
// and truncated to MAX_STRING_LENGTH. On serialization failure we return a sentinel string.
function toMeticulousValue(value: unknown): boolean | string {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH - 3)}...` : value;
  }

  try {
    const json = JSON.stringify(value);
    if (json === undefined) {
      return 'unserializable';
    }
    return json.length > MAX_STRING_LENGTH ? `${json.slice(0, MAX_STRING_LENGTH - 3)}...` : json;
  } catch {
    return 'unserializable';
  }
}

/**
 * Record the flag value once per page load. Intended to be called from an LD 'flag-used' inspector.
 * Work is scheduled on a microtask to avoid blocking render/evaluation.
 */
export function recordFlagToMeticulous(flagKey: string, value: unknown): void {
  if (recordedThisPageLoad.has(flagKey)) {
    return;
  }
  if (recordedThisPageLoad.size >= MAX_FLAGS_PER_LOAD) {
    return;
  }

  recordedThisPageLoad.add(flagKey);

  // Offload to microtask to be extra careful we're not slowing things down
  queueMicrotask(() => {
    try {
      const normalized = toMeticulousValue(value);
      window.Meticulous?.context?.recordFeatureFlag?.(flagKey, normalized);
    } catch {
      // do nothing; Meticulous just won't get flag values
    }
  });
}

// Test-only hook to reset the per-load dedupe set.
export function resetMeticulousFlagRecorderForTests() {
  recordedThisPageLoad.clear();
}
